import React, { useEffect, useState, useMemo } from "react";
import {
  Checkbox,
  Button,
  Box,
  Card,
  Pagination,
  Typography,
  IconButton,
  TextField,
  Alert,
} from "@mui/material";
import Toast from "../../common/Toast";
import DataTable from "../../common/DataTable";
import { ShipmentType } from "../../types/Shipment";
import {
  fetchShipmentListingData,
  updateShipments,
} from "../../api/ShipmentDataAPI";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../common/Breadcrumbs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PencilIcon from "../../assets/pencil-icon.svg";

interface EditableFieldProps {
  value: string;
  onSave: (newValue: string) => void;
  onCancel?: () => void;
  field: string;
  isEditing: boolean;
  onEditClick: () => void;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onSave,
  onCancel,
  field,
  isEditing,
  onEditClick,
}) => {
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
  };

  const handleCancel = () => {
    setEditValue(value);
    onCancel?.();
  };

  return isEditing ? (
    <Box sx={{ display: "flex" }}>
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        size="small"
        sx={{
          padding: "1px",
          fontSize: "12px",
          "& .MuiInputBase-input": {
            fontSize: "11.63px",
            padding: "4px",
          },
        }}
      />
      <IconButton
        size="small"
        onClick={handleSave}
        aria-label={`Save ${field}`}
      >
        <CheckCircleIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleCancel}
        aria-label={`Cancel ${field}`}
      >
        <CancelIcon fontSize="small" />
      </IconButton>
    </Box>
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", fontSize: "11.63px" }}>
      {value}
      <IconButton size="small" onClick={onEditClick} sx={{ marginLeft: 1 }}>
        <img src={PencilIcon} alt="Edit" style={{ width: 16, height: 16 }} />
      </IconButton>
    </Box>
  );
};

const areAllFieldsFilled = (
  orders: ShipmentType[],
  editedFields: Record<string, Partial<ShipmentType>>
) => {
  return orders.every((order) => {
    const editedData = editedFields[order.orderDetails.orderId] || {};

    const truckNo = editedData.truckNo || order.truckNo;
    const driverName = editedData.driverName || order.driverName;
    const phone = editedData.phone || order.phone;

    return truckNo && driverName && phone;
  });
};

const Shipment: React.FC = () => {
  const [shipmentData, setShipmentData] = useState<ShipmentType[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<ShipmentType[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 3,
    totalRecords: 0,
  });

  const [editingField, setEditingField] = useState<{
    id: string;
    field: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editedFields, setEditedFields] = useState<
    Record<string, Partial<ShipmentType>>
  >({});

  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        label: "",
        field: "checkbox",
        renderCell: (row: ShipmentType) => (
          <Checkbox
            checked={selectedOrders.some(
              (shipment) =>
                shipment.orderDetails.orderId === row.orderDetails.orderId
            )}
            onChange={() => handleCheckboxChange(row)}
          />
        ),
      },
      { label: "Customer", field: "customer" },
      { label: "Location", field: "location" },
      {
        label: "Order Details",
        field: "orderDetails",
        renderCell: (row: ShipmentType) => (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" style={{ fontSize: "10.66px" }}>
              {row.orderDetails.orderId}
            </Typography>
            <Typography
              variant="body2"
              style={{ fontSize: "11.63px", fontWeight: 600 }}
            >
              {row.orderDetails.price}
            </Typography>
          </Box>
        ),
      },
      { label: "Payment", field: "payment" },
      { label: "ETA", field: "ETA" },
      { label: "Order Status", field: "orderStatus" },
      {
        label: "Truck No",
        field: "truckNo",
        renderCell: (row: ShipmentType) => (
          <EditableField
            value={
              editedFields[row.orderDetails.orderId]?.truckNo ||
              row.truckNo ||
              ""
            }
            field="truckNo"
            isEditing={
              editingField?.id === row.orderDetails.orderId &&
              editingField.field === "truckNo"
            }
            onEditClick={() =>
              setEditingField({
                field: "truckNo",
                id: row.orderDetails.orderId,
              })
            }
            onSave={(newValue) => {
              setEditedFields((prev) => ({
                ...prev,
                [row.orderDetails.orderId]: {
                  ...prev[row.orderDetails.orderId],
                  truckNo: newValue,
                },
              }));
              setEditingField(null);
            }}
            onCancel={() => setEditingField(null)}
          />
        ),
      },
      {
        label: "Driver Name",
        field: "driverName",
        renderCell: (row: ShipmentType) => (
          <EditableField
            value={
              editedFields[row.orderDetails.orderId]?.driverName ||
              row.driverName ||
              ""
            }
            field="driverName"
            isEditing={
              editingField?.id === row.orderDetails.orderId &&
              editingField.field === "driverName"
            }
            onEditClick={() =>
              setEditingField({
                field: "driverName",
                id: row.orderDetails.orderId,
              })
            }
            onSave={(newValue) => {
              setEditedFields((prev) => ({
                ...prev,
                [row.orderDetails.orderId]: {
                  ...prev[row.orderDetails.orderId],
                  driverName: newValue,
                },
              }));
              setEditingField(null);
            }}
            onCancel={() => setEditingField(null)}
          />
        ),
      },
      {
        label: "Phone Number",
        field: "phone",
        renderCell: (row: ShipmentType) => (
          <EditableField
            value={
              editedFields[row.orderDetails.orderId]?.phone || row.phone || ""
            }
            field="phone"
            isEditing={
              editingField?.id === row.orderDetails.orderId &&
              editingField.field === "phone"
            }
            onEditClick={() =>
              setEditingField({ field: "phone", id: row.orderDetails.orderId })
            }
            onSave={(newValue) => {
              setEditedFields((prev) => ({
                ...prev,
                [row.orderDetails.orderId]: {
                  ...prev[row.orderDetails.orderId],
                  phone: newValue,
                },
              }));
              setEditingField(null);
            }}
            onCancel={() => setEditingField(null)}
          />
        ),
      },
    ],
    [selectedOrders, editingField, editedFields]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, totalRecords } = await fetchShipmentListingData(
          pagination.page,
          pagination.rowsPerPage
        );

        setShipmentData(data);

        setPagination((prev) => ({
          ...prev,
          totalRecords,
        }));
      } catch (error) {
        console.error("Error fetching shipment data:", error);
        setErrorMessage("Failed to load shipment data.");
      }
    };

    fetchData();
  }, [pagination.page, pagination.rowsPerPage]);

  const handleCheckboxChange = (row: ShipmentType): void => {
    const allFieldsFilled = areAllFieldsFilled([row], editedFields);

    if (!allFieldsFilled) {
      setToastMessage(
        "All fields (Truck No, Driver Name, Phone) must be filled!"
      );
      setError(true);
      setShowToast(true);
      return;
    }

    const exists = selectedOrders.some(
      (shipment) => shipment.orderDetails.orderId === row.orderDetails.orderId
    );

    if (exists) {
      setSelectedOrders((prev) =>
        prev.filter(
          (shipment) =>
            shipment.orderDetails.orderId !== row.orderDetails.orderId
        )
      );
    } else {
      setSelectedOrders((prev) => [...prev, row]);
    }
  };

  const handleCreateSummary = async (): Promise<void> => {
    // Use the helper function to check if all fields are filled for selected orders
    const allFieldsFilled = areAllFieldsFilled(selectedOrders, editedFields);

    if (!allFieldsFilled) {
      setToastMessage("Kindly add all the required fields.");
      setError(true);
      setShowToast(true);
      return;
    }

    try {
      const updatedSelectedOrders = selectedOrders.map((order) => ({
        ...order,
        ...editedFields[order.orderDetails.orderId],
      }));

      await updateShipments(updatedSelectedOrders);

      setToastMessage("Shipments updated successfully!");
      setError(false);
      setShowToast(true);
      navigate("shipment-summary", {
        state: { selectedOrders: updatedSelectedOrders },
      });
    } catch (error) {
      console.error("Error updating shipment data:", error);
      setToastMessage("Kindly add all the required fields.");
      setError(true);
      setShowToast(true);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const { page, rowsPerPage } = pagination;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(
    startIndex + rowsPerPage,
    startIndex + shipmentData.length
  );
  const currentRows = shipmentData.slice(startIndex, endIndex);
  const emptyRows = rowsPerPage - currentRows.length;

  return (
    <>
      <Breadcrumbs paths={["Shipment"]} links={["/seller/shipment"]} />
      <Box>
        <Card sx={{ paddingTop: 4, paddingBottom: 1, boxShadow: 6 }}>
          <DataTable
            alternateBg
            columns={columns.map((col) => ({
              ...col,
              labelStyle: { fontSize: "14px", color: "#151C28" },
              cellStyle:
                col.field === "customer"
                  ? { fontWeight: 600, fontSize: "13px" }
                  : { fontSize: "11.63px" },
            }))}
            data={shipmentData}
            emptyStateMessage="No shipments available"
            rowsPerPage={rowsPerPage}
            emptyRows={emptyRows}
          />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={selectedOrders.length === 0}
              onClick={handleCreateSummary}
              sx={{
                width: "auto",
                padding: "8px 16px",
                marginRight: "16px",
              }}
            >
              Create Shipment Summary
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "#B5B7C0", fontFamily: "Poppins, sans-serif" }}
            >
              Showing data {startIndex + 1} to {endIndex} of{" "}
              {pagination.totalRecords} entries
            </Typography>
            <Pagination
              count={Math.ceil(
                pagination.totalRecords / pagination.rowsPerPage
              )}
              page={pagination.page}
              onChange={(_, newPage) => handleChangePage(newPage)}
              shape="rounded"
              variant="outlined"
            />
          </Box>
        </Card>
        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          toastMessage={toastMessage}
          error={error}
        />
      </Box>
    </>
  );
};

export default Shipment;
